/**
 * One file in the cart: a line you can read at a glance, and two panels you
 * open when you want to change something.
 *
 * THE OPTIONS PANEL IS OPEN FROM THE START. It used to be shut behind a bare
 * chevron, and the chevron read as an afterthought because it was one: a
 * nameless glyph is not an invitation, and a customer who never pressed it
 * never discovered that double-sided is half the price. Open by default makes
 * the choice the screen is for the thing you actually see. The cost of that is
 * height, which is why everything inside it is compact and why binding — the
 * choice most files never make — sits behind its own named button.
 *
 * THERE ARE TWO BUTTONS, NOT ONE CHEVRON. "Print options" and "Binding" say
 * what they open. Binding is disabled when nothing we offer takes a book this
 * size, and it SAYS WHY: a one-page receipt is too thin for every binding in
 * the shop, and a dead control with no explanation sends the customer to the
 * phone.
 *
 * THE PRESET CARDS COST NOTHING TO SHOW. Their prices come down with the cart
 * row itself — see `CartPresetOut` — so ten files with ten open panels make no
 * pricing requests at all. The binding panel still asks when it opens, which
 * is the customer's own doing and happens once.
 *
 * WHAT A CARD SHOWS IS THIS FILE'S OWN COST, never a share of the order.
 * Delivery and tax belong to the parcel and cannot honestly be divided
 * between documents, so they appear once, at the bottom of the screen.
 */
import { useId, useMemo, useState } from 'react';

import { Alert, Badge, Button, Checkbox, Spinner } from '@/components/ui';
import { BindingPicker } from '@/features/pricing/BindingPicker';
import { CopiesField } from '@/features/pricing/CopiesField';
import { CoverColourPicker } from '@/features/pricing/CoverColourPicker';
import { PresetPicker } from '@/features/pricing/PresetPicker';
import { choicesFromCartItem } from '@/features/pricing/presets';
import { formatPageScope, issueTitle } from '@/features/validation/issues';
import { cn } from '@/lib/cn';
import { useBindingPrices } from '@/lib/api/quotes';
import type {
  CartItemUpdate,
  CartItemOut,
  CoverColour,
  DocumentRequest,
  PrintOptionOut,
} from '@/lib/api/types';
import { formatPages, formatPaise, sheetsForPages } from '@/lib/format';

export interface FileRowProps {
  item: CartItemOut;
  /** The preset catalogue, fetched once for the whole screen. */
  printOptions: readonly PrintOptionOut[];
  /** The rest of the cart, held fixed while this file's bindings are priced. */
  others: DocumentRequest[];
  coverColours: CoverColour[];
  onChange: (patch: CartItemUpdate) => void;
  onRemove: () => void;
  /** True while a change to THIS row is in flight. Dims, never blocks. */
  busy?: boolean;
}

/**
 * "10 pages · B/W, single-sided · Spiral" — the whole closed line.
 *
 * NOT THE COPIES. They have their own stepper two lines down, always on
 * screen, and the same number twice on one row is noise — the one that is a
 * live control wins.
 */
function summaryLine(item: CartItemOut): string {
  const parts: string[] = [];
  if (item.page_count !== null) parts.push(formatPages(item.page_count));
  if (item.print_option) {
    parts.push(item.print_option.label);
    if (item.is_default_option) parts.push('default');
  }
  if (item.binding && item.binding.price_paise > 0) parts.push(item.binding.label);
  return parts.join(' · ');
}

export function FileRow({
  item,
  printOptions,
  others,
  coverColours,
  onChange,
  onRemove,
  busy = false,
}: FileRowProps) {
  const optionsPanelId = useId();
  const bindingPanelId = useId();
  const bindingReasonId = useId();
  const rejected = item.reason === 'rejected';
  const validating = item.reason === 'validating';

  // Open from the start, shut only if the customer shuts it. Held here and
  // not by the screen above, because every row can now be open at once —
  // "one at a time" was a way of keeping the list short and is what being
  // open by default gives up.
  const [optionsOpen, setOptionsOpen] = useState(true);
  const [bindingOpen, setBindingOpen] = useState(false);

  const choices = useMemo(() => choicesFromCartItem(item, printOptions), [item, printOptions]);
  const selected = choices.find((choice) => choice.slug === item.print_option?.slug);

  const sided = item.print_option?.sided === 2 ? 2 : 1;
  const sheetsPerCopy = sheetsForPages(item.page_count ?? 0, sided);
  // Only once the panel is open, and only while it stays open: this is the
  // one request a row still makes, and the customer asked for it.
  const bindingPrices = useBindingPrices({
    uploadId: bindingOpen && item.priceable ? item.upload_id : undefined,
    copies: item.copies,
    printOptionSlug: item.print_option?.slug,
    others,
  });

  const pending = item.issues.filter(
    (issue) => issue.acknowledgeable && !item.acknowledged_codes.includes(issue.code),
  );
  const blocking = item.issues.find((issue) => !issue.acknowledgeable);

  return (
    <li
      className={cn(
        'border-b border-line last:border-b-0',
        busy && 'opacity-60 transition-opacity',
      )}
      data-testid="cart-file-row"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-sm font-medium text-ink">{item.filename}</span>
            {item.line_total_paise !== null ? (
              <span className="shrink-0 text-sm font-medium tabular-nums text-ink">
                {formatPaise(item.line_total_paise)}
              </span>
            ) : null}
          </div>

          <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-subtle">
            {validating ? (
              <>
                <Spinner size="sm" label="" />
                {/* Not "0 pages": nothing is known about it yet, and a zero
                    reads as an empty file rather than an unread one. */}
                <span>Checking your file…</span>
              </>
            ) : rejected ? (
              <Badge tone="danger">Cannot print</Badge>
            ) : (
              // ONE SPAN, not three. As separate flex children each of
              // "10 pages", "1 copy" and the summary wrapped onto its own
              // second line at 375px, turning a one-line row into four. Joined,
              // the line truncates from the right as it was meant to — and the
              // first thing to go is the end of the preset name, which the
              // open panel below is already showing.
              <span className="truncate" data-testid="cart-file-summary">
                {summaryLine(item)}
              </span>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          onClick={onRemove}
          aria-label={`Remove ${item.filename}`}
          className="shrink-0 px-2"
        >
          ✕
        </Button>
      </div>

      {item.priceable ? (
        <div className="px-4 pb-3">
          {/* COPIES ARE NEVER BEHIND A BUTTON. It is the one setting somebody
              changes without thinking about printing at all — and the one
              whose being wrong is only discovered when forty sheets come off
              the machine. The preset and the binding are decisions; this is a
              quantity, and a quantity belongs on the row. */}
          <CopiesField
            value={item.copies}
            onChange={(copies) => onChange({ copies })}
            disabled={busy}
            compact
          />
        </div>
      ) : null}

      {item.priceable ? (
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
          <Button
            variant="secondary"
            onClick={() => setOptionsOpen((open) => !open)}
            aria-expanded={optionsOpen}
            aria-controls={optionsPanelId}
            className="px-3 py-1.5 text-sm"
          >
            Print options
          </Button>

          <Button
            variant="secondary"
            onClick={() => setBindingOpen((open) => !open)}
            disabled={!item.bindable}
            aria-expanded={item.bindable ? bindingOpen : undefined}
            aria-controls={item.bindable ? bindingPanelId : undefined}
            // The reason is the button's own description, so a screen reader
            // reaching a disabled control is told why in the same breath
            // rather than finding the sentence beside it by luck.
            aria-describedby={item.bindable ? undefined : bindingReasonId}
            className="px-3 py-1.5 text-sm"
          >
            Binding
          </Button>

          {!item.bindable && item.binding_block_reason ? (
            <p id={bindingReasonId} className="w-full text-xs text-ink-subtle">
              {item.binding_block_reason}
            </p>
          ) : null}
        </div>
      ) : null}

      {rejected ? (
        <div className="px-4 pb-3">
          {/* The ISSUE's own title, not a fixed one. "This file is password
              protected" and "The pages are not all the same size" are
              different problems with different fixes, and a single "We
              cannot print this file" for both tells the customer nothing
              they can act on. Same reasoning for the page scope below: the
              review screen named the offending pages, and a customer with a
              sixty-page document needs to know it is pages 3 and 5. */}
          <Alert
            severity="danger"
            title={blocking ? issueTitle(blocking) : 'We cannot print this file'}
          >
            {/* The server's own words: they say what is wrong AND what to
                do about it, and paraphrasing loses the second half. */}
            <p>
              {blocking?.message ??
                'This file cannot be printed as it is. Please upload a corrected version.'}
            </p>
            {blocking?.pages?.length ? (
              <p className="mt-1 text-sm">{formatPageScope(blocking.pages)}</p>
            ) : null}
            <p className="mt-2">
              <Button variant="secondary" onClick={onRemove}>
                Remove it
              </Button>
            </p>
          </Alert>
        </div>
      ) : null}

      {item.converted_from ? (
        <div className="px-4 pb-3">
          {/* The one thing a Word upload needs said out loud: what we print
              is the CONVERSION, not the file sitting on their machine. A
              font we do not have is substituted and the text reflows, and
              nothing catches that except somebody looking. It used to live
              on the review screen, which this row replaced. */}
          <Alert severity="info" title={`Converted from your ${item.converted_from}`}>
            <p>
              We turned it into a PDF, and that is exactly what we will print. If a font was missing
              at our end the layout can shift, so it is worth a look.
            </p>
          </Alert>
        </div>
      ) : null}

      {pending.length > 0 ? (
        <div className="space-y-3 px-4 pb-3">
          {pending.map((issue) => (
            <Alert key={issue.code} severity="warning" title={issueTitle(issue)}>
              <p>{issue.message}</p>
              {issue.pages?.length ? (
                <p className="mt-1 text-sm">{formatPageScope(issue.pages)}</p>
              ) : null}
              <div className="mt-2">
                <Checkbox
                  name={`confirm-${item.id}-${issue.code}`}
                  checked={false}
                  // Disabled while the confirmation is in flight. It cannot
                  // show itself ticked — the whole warning disappears once
                  // the server has it — so without this a slow connection
                  // looks like a dead control and invites a second click.
                  disabled={busy}
                  onChange={() =>
                    onChange({
                      acknowledged_codes: [...item.acknowledged_codes, issue.code],
                    })
                  }
                  label="Print it anyway"
                />
              </div>
            </Alert>
          ))}
        </div>
      ) : null}

      {optionsOpen && item.priceable ? (
        <div
          id={optionsPanelId}
          className="space-y-3 border-t border-line bg-surface-muted px-4 py-3"
        >
          {/* THE PAPER WARNING, once above the cards rather than only on
              each of them. A customer whose file is not the size we stock
              needs to know their pages will be resized BEFORE they compare
              presets, not on whichever card they happen to look at.

              Read off the SELECTED preset, because which paper a file lands
              on is a property of the preset: with mixed stock two presets can
              answer differently for the same document.

              Their own size is deliberately not named: "A5" is our
              vocabulary, not theirs, and it is not the actionable half —
              what they need is that it is not the paper we print on. */}
          {selected?.documentSizeLabel ? (
            <Alert severity="warning" title={`This file is not ${selected.sizeLabel}`}>
              <p>We print on {selected.sizeLabel}, so the pages are resized to fit.</p>
            </Alert>
          ) : null}

          {/* The catalogue is one cached request for the whole screen, so this
              is a flash on a cold load rather than a state anybody sits in —
              but an empty "How should we print it?" fieldset is a worse flash
              than a line of text. */}
          {choices.length > 0 ? (
            <PresetPicker
              choices={choices}
              value={item.print_option?.slug ?? null}
              onChange={(slug) => onChange({ print_option_slug: slug })}
              refreshing={busy}
            />
          ) : (
            <p className="flex items-center gap-2 text-xs text-ink-subtle">
              <Spinner size="sm" label="" />
              Loading the ways we can print it…
            </p>
          )}
        </div>
      ) : null}

      {bindingOpen && item.priceable ? (
        <div
          id={bindingPanelId}
          className="space-y-4 border-t border-line bg-surface-muted px-4 py-3"
        >
          <BindingPicker
            bindings={bindingPrices.data?.bindings ?? []}
            value={item.binding?.slug ?? null}
            onChange={(slug) => onChange({ binding_slug: slug })}
            sheetsPerCopy={sheetsPerCopy}
            copies={item.copies}
            refreshing={bindingPrices.isFetching}
          />

          {bindingPrices.isLoading ? (
            <p className="flex items-center gap-2 text-xs text-ink-subtle">
              <Spinner size="sm" label="" />
              Working out what binding costs…
            </p>
          ) : null}

          {item.binding?.has_cover ? (
            <CoverColourPicker
              colours={coverColours}
              value={item.cover_colour}
              onChange={(slug) => onChange({ cover_colour: slug })}
              bindingLabel={item.binding.label}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
